import React, { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { useEffect } from "react";
import { useMap } from "../hooks";
import { useLazyRef } from "../hooks/useLazyRef";
import ImageLayer from "ol/layer/Image";
import { ImageStatic } from "ol/source";
import { Location } from "../MapContainer";

/**
 * 정적 이미지 오버레이 props — 드론 정사영상(orthoimage) 등을 지도 위에 표시.
 *
 * `bounds` 는 항상 `EPSG:4326` (경도/위도) 좌표.
 * `MapContainer` 또는 `SyncMap` 내부에서만 사용 가능.
 */
export interface ImageOverlayProps {
  imageUrl: string;
  altText?: string;
  zIndex?: number;
  /** `[[minX, minY], [maxX, maxY]]` — EPSG:4326 좌표 */
  bounds: Location[];
  /**
   * 이미지 적재 상태 통지. 실패를 성공과 구분해 올린다 — 실패를 "적재 중 아님"으로만
   * 알리면 호출자가 그림이 얹힌 줄 알고 빈 지도를 드러낸다.
   */
  onLoadStateChange?: (state: ImageOverlayLoadState) => void;
}

/** 적재가 시작됐다 · 그려졌다 · 실패했다 — 세 상태를 구분해 올린다 */
export type ImageOverlayLoadState = "loading" | "drawn" | "failed";

/** ref 핸들 — 레이어를 지도에서 동적으로 제거/재추가. */
export interface ImageOverlayRef {
  removeFrom: () => void;
  addTo: () => void;
}

export const ImageOverlay = forwardRef<ImageOverlayRef, ImageOverlayProps>(
  ({ imageUrl, zIndex = 0, bounds, onLoadStateChange }, ref) => {
    const map = useMap();
    // useRef(new ImageLayer()) 는 매 렌더마다 throwaway 인스턴스를 생성한다 — lazy-init(D7).
    const imageRef = useLazyRef<ImageLayer<ImageStatic>>(() => new ImageLayer());

    const removeFrom = useCallback(() => {
      if (imageRef.current) {
        map.removeLayer(imageRef.current);
      }
    }, [map]);

    const addTo = useCallback(() => {
      if (imageRef.current) {
        map.addLayer(imageRef.current);
      }
    }, [map]);

    useImperativeHandle(
      ref,
      () => {
        return {
          removeFrom,
          addTo,
        };
      },
      [removeFrom, addTo]
    );

    // 적재 통지는 최신 콜백으로 부르되, 콜백 신원 변화가 소스를 다시 만들지 않게 한다 —
    // 재생성은 곧 이미지 재요청이라, 통지를 받아 상태를 바꾸는 호출자가 재요청 고리에 빠진다.
    const onLoadStateChangeRef = useRef(onLoadStateChange);
    useEffect(() => {
      onLoadStateChangeRef.current = onLoadStateChange;
    }, [onLoadStateChange]);

    useEffect(() => {
      function handleLoadStart() {
        onLoadStateChangeRef.current?.("loading");
      }
      function handleLoadEnd() {
        onLoadStateChangeRef.current?.("drawn");
      }
      function handleLoadError() {
        onLoadStateChangeRef.current?.("failed");
      }
      const imageStatic = new ImageStatic({
        url: imageUrl,
        imageExtent: bounds.flat(),
        projection: "EPSG:4326",
      });

      imageStatic.on("imageloadstart", handleLoadStart);
      imageStatic.on("imageloadend", handleLoadEnd);
      imageStatic.on("imageloaderror", handleLoadError);

      if (imageRef.current) {
        imageRef.current.setSource(imageStatic);
      }

      return () => {
        if (imageRef.current) {
          imageRef.current.setSource(null);
        }
        imageStatic.un("imageloadstart", handleLoadStart);
        imageStatic.un("imageloadend", handleLoadEnd);
        imageStatic.un("imageloaderror", handleLoadError);
      };
    }, [bounds, imageUrl]);

    useEffect(() => {
      if (imageRef.current) {
        imageRef.current.setZIndex(zIndex);
      }
    }, [zIndex]);

    useEffect(() => {
      const imageLayer = imageRef.current;
      if (imageLayer) {
        map.addLayer(imageLayer);
      }

      return () => {
        if (imageLayer) {
          map.removeLayer(imageLayer);
        }
      };
    }, []);
    return <div></div>;
  }
);

ImageOverlay.displayName = "ImageOverlay";
